import { useState, useEffect, useRef } from 'react';
import { B, radius, font, weight, space } from '../../constants/brand';
import { Icon } from '../ui';
import { ChatPanel } from './ChatPanel';

// =============================================================
// ChatWidget — the always-available Harvi, in the corner of every page.
//
// IT IS A FRAME AROUND ChatPanel AND NOTHING ELSE. It used to be a whole
// second assistant: ~370 lines that posted to the legacy bot endpoint
// (botCore), which is one endless log keyed to the signed-in user with no
// threads, no visibility, no draft cards and no provenance. The good assistant
// — /api/conversations, the scoped tool layer, propose-don't-commit — existed
// only on a deal's Chat tab, so the Harvi people actually clicked was the old
// one. Two implementations of one feature is how they came to differ at all,
// so this one was deleted rather than caught up. iOS made the same move in
// mobile/app/(tabs)/harvi.tsx.
//
// The surface is 'General': no record, threads private by default, and the
// only place mail and calendar can run (personalToolRefusal requires General
// AND Private). The legacy bot route still exists — Teams and SMS answer
// through it.
//
// WHAT THIS DELIBERATELY DOES NOT CARRY OVER: the old bot's markdown deep
// links and the click handler that turned them into in-app navigation. The
// assistant answers in plain text with proposal cards, so there are no anchors
// to intercept; keeping the handler would be code guarding a case that cannot
// occur. The `harvi-navigate` event itself is untouched — Contacts.jsx still
// dispatches it and App.jsx still listens.
//
// THE FRAME OWNS NO LAYOUT BEYOND ITS OWN BOX. It used to wrap the panel in a
// second scrolling div, so the panel's transcript scrolled inside a scroller
// that also scrolled — and the composer, being at the bottom of the inner one,
// slid off the end of a long thread. The panel is now a self-contained column
// (header / transcript / composer) handed `fill`, and this file sizes the box
// and nothing else.
// =============================================================

const SIZES = {
  normal:   { w: 'min(760px, calc(100vw - 32px))',  h: 'min(680px, calc(100vh - 120px))' },
  expanded: { w: 'min(1140px, calc(100vw - 32px))', h: 'min(880px, calc(100vh - 96px))'  },
};

// ON A PHONE THIS IS A FULL-SCREEN APP, NOT A FLOATING CARD. A 680px panel
// inset from the corner of a 850px-tall phone leaves the page showing down both
// sides and under the composer, so a thumb aiming at the transcript scrolls the
// deal list behind it; the panel gets a fraction of the screen and the keyboard
// takes most of what is left. Every assistant people use goes full-bleed here,
// and so does this. The breakpoint is the VIEWPORT (this element is fixed to
// it) — unlike ChatPanel's rail, which measures itself.
const PHONE_MAX_WIDTH = 640;

const useIsPhone = () => {
  const [phone, setPhone] = useState(
    typeof window !== 'undefined' && window.innerWidth <= PHONE_MAX_WIDTH);
  useEffect(() => {
    const onResize = () => setPhone(window.innerWidth <= PHONE_MAX_WIDTH);
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return phone;
};

export function ChatWidget({ openConversation = null }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // ChatPanel holds the thread state, and unmounting it on close would throw
  // away an in-flight answer and re-fetch the list on every reopen. So it is
  // mounted from the first open onwards and hidden thereafter.
  const [mounted, setMounted] = useState(false);
  const [hovered, setHovered] = useState(false);
  const isPhone = useIsPhone();
  const rootRef = useRef(null);

  useEffect(() => { if (open) setMounted(true); }, [open]);

  // A notification that points at a Harvi thread (the morning brief) opens the
  // widget; ChatPanel selects the thread from the same prop.
  useEffect(() => {
    if (openConversation?.id) setOpen(true);
  }, [openConversation?.id, openConversation?.n]);

  // The header's Ask Harvi box: open the widget and send the question.
  const [askRequest, setAskRequest] = useState(null);
  useEffect(() => {
    const onAsk = (e) => {
      const text = String(e?.detail?.text || '').trim();
      if (!text) return;
      setOpen(true);
      setAskRequest((prev) => ({ text, n: (prev?.n || 0) + 1 }));
    };
    window.addEventListener('harvi-ask', onAsk);
    return () => window.removeEventListener('harvi-ask', onAsk);
  }, []);

  // A full-screen sheet over a scrolling page must stop the page scrolling
  // underneath it, or a swipe on the transcript moves the deal list behind.
  useEffect(() => {
    if (!open || !isPhone) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open, isPhone]);

  // Escape closes, the way every other overlay in the app behaves. Bound only
  // while open so it cannot swallow Escape from a modal underneath, and NOT
  // capturing, so the panel's own popovers close first.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // A click anywhere outside the widget collapses it, same as NotificationBell's
  // dropdown. Skipped on the phone sheet — it is full-bleed, so there is no
  // "outside" on the page to click.
  useEffect(() => {
    if (!open || isPhone) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, isPhone]);

  const size = expanded ? SIZES.expanded : SIZES.normal;

  return (
    <div ref={rootRef} style={{
      position: 'fixed', zIndex: 1000, pointerEvents: 'none',
      display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12,
      // Longhands, not `inset: 0` — the shorthand is newer than some of the
      // browsers this runs in, and it is the one that silently does nothing.
      ...(isPhone && open
        ? { top: 0, right: 0, bottom: 0, left: 0 }
        : { bottom: isPhone ? 16 : 24, right: isPhone ? 16 : 24 }),
    }}>
      {mounted && (
        <div
          role="dialog"
          aria-label="Harvi assistant"
          aria-hidden={!open}
          style={{
            display: open ? 'flex' : 'none',
            flexDirection: 'column',
            background: B.surface,
            overflow: 'hidden',
            pointerEvents: 'auto',
            ...(isPhone
              ? {
                // 100dvh, not 100vh: on iOS Safari 100vh is the height WITHOUT
                // the browser bars, so the composer sits under the address bar.
                width: '100%', height: '100dvh', maxHeight: '100dvh',
                borderRadius: 0, border: 'none', boxShadow: 'none',
                paddingBottom: 'env(safe-area-inset-bottom)',
              }
              : {
                width: size.w, height: size.h,
                borderRadius: 20, boxShadow: B.shadowXl, border: `1px solid ${B.divider}`,
                animation: open ? 'harvus-pop-in 0.2s cubic-bezier(0.22,1,0.36,1)' : undefined,
              }),
          }}
        >
          <div style={{
            background: B.navy, backgroundImage: B.gradBrand,
            padding: `${space.md}px ${space.lg}px`, display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', flexShrink: 0, gap: space.sm,
            paddingTop: isPhone ? `calc(${space.md}px + env(safe-area-inset-top))` : space.md,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: '50%',
                background: 'rgba(255,255,255,0.14)', color: B.teal,
              }}><Icon name="sparkles" size={16} /></span>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: B.white, fontWeight: weight.bold, fontSize: font.md, letterSpacing: -0.2 }}>Harvi</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: font.xs }}>Your deal book, answered</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {!isPhone && (
                <HeaderButton
                  icon={expanded ? 'minimize' : 'maximize'}
                  label={expanded ? 'Shrink Harvi' : 'Expand Harvi'}
                  onClick={() => setExpanded(v => !v)}
                />
              )}
              <HeaderButton icon="close" label="Close Harvi" onClick={() => setOpen(false)} />
            </div>
          </div>

          {/* The panel fills the frame and owns its own scrolling. */}
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <ChatPanel general fill paused={!open} openConversation={openConversation} askRequest={askRequest} />
          </div>
        </div>
      )}

      {!(isPhone && open) && (
      <button
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-label={open ? 'Close Harvi' : 'Open Harvi'}
        aria-expanded={open}
        style={{
          height: 56, minWidth: 56, padding: open || !hovered ? 0 : '0 20px 0 18px',
          borderRadius: 28,
          background: B.navy, backgroundImage: B.gradBrand,
          border: 'none', cursor: 'pointer', pointerEvents: 'auto',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
          color: B.white, fontFamily: 'inherit', fontSize: font.md, fontWeight: weight.semibold,
          boxShadow: hovered
            ? '0 14px 34px rgba(35,76,90,0.42), 0 3px 8px rgba(26,46,53,0.18)'
            : '0 10px 26px rgba(35,76,90,0.34), 0 2px 6px rgba(26,46,53,0.14)',
          transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
          transition: 'transform 0.18s cubic-bezier(0.22,1,0.36,1), box-shadow 0.18s, padding 0.18s',
        }}
      >
        <span style={{ display: 'flex', color: B.teal, lineHeight: 1 }}>
          <Icon name={open ? 'close' : 'sparkles'} size={22} />
        </span>
        {/* The label only unfurls on hover — a permanent pill is a permanent
            advert, and a bare circle tells a first-time user nothing. */}
        {!open && hovered && <span style={{ whiteSpace: 'nowrap' }}>Ask Harvi</span>}
      </button>
      )}
    </div>
  );
}

const HeaderButton = ({ icon, label, onClick }) => (
  <button
    onClick={onClick}
    aria-label={label}
    title={label}
    style={{
      background: 'none', border: 'none', cursor: 'pointer', display: 'flex',
      color: 'rgba(255,255,255,0.65)', lineHeight: 1, padding: 6,
      borderRadius: radius.lg, fontFamily: 'inherit', transition: 'background .15s, color .15s',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; e.currentTarget.style.color = '#fff'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
  ><Icon name={icon} size={17} /></button>
);

export default ChatWidget;
